// src/edu-world/systems/zone-transition.ts
// Zone Transition System — detects which zone the player is in based on grid position,
// emits zone:changed events on transitions, and manages placeholder zone tracking.
// Requirements: 1.6 (seamless transitions), 1.8–1.10 (placeholder fallback)

import type { EventBus } from '../core/event-bus';

/**
 * Zone definition from the parque.json grid config.
 * Each zone occupies a cell in a grid overlaying the world map.
 */
export interface ZoneGridEntry {
  id: string;
  name: string;
  gridX: number;
  gridY: number;
  background: string;
}

/**
 * ZoneTransitionSystem
 *
 * Tracks the player's current zone based on their world-space position
 * projected onto the zone grid. Emits 'zone:changed' events when the
 * player crosses a zone boundary. Also tracks which zones are in
 * placeholder state (failed asset load) to inform guidance systems.
 *
 * The world map is divided into a grid (cols × rows), where each cell
 * corresponds to one thematic zone. With a unified background image,
 * zone transitions are inherently seamless (no loading) — this system
 * provides logical zone tracking for gameplay systems.
 */
export class ZoneTransitionSystem {
  private eventBus: EventBus;
  private zones: ZoneGridEntry[];
  private gridCols: number;
  private gridRows: number;
  private worldWidth: number;
  private worldHeight: number;

  private currentZoneId: string | null = null;
  private placeholderZones: Set<string> = new Set();

  constructor(
    eventBus: EventBus,
    zones: ZoneGridEntry[],
    gridCols: number,
    gridRows: number,
    worldWidth: number,
    worldHeight: number
  ) {
    this.eventBus = eventBus;
    this.zones = zones;
    this.gridCols = gridCols;
    this.gridRows = gridRows;
    this.worldWidth = worldWidth;
    this.worldHeight = worldHeight;
  }

  /**
   * Update with the player's current world position.
   * Should be called every frame (or at least when position changes).
   * Emits 'zone:changed' only when the zone actually changes.
   */
  update(playerX: number, playerY: number): void {
    const zoneId = this.getZoneAtPosition(playerX, playerY);
    if (zoneId && zoneId !== this.currentZoneId) {
      const previousZone = this.currentZoneId;
      this.currentZoneId = zoneId;

      const zone = this.zones.find((z) => z.id === zoneId);
      this.eventBus.emit('zone:changed', {
        previous_zone: previousZone,
        current_zone: zoneId,
        zone_name: zone?.name ?? zoneId,
      });
    }
  }

  /**
   * Determine which zone contains the given world-space position.
   * Maps continuous coordinates to the discrete zone grid.
   */
  getZoneAtPosition(x: number, y: number): string | null {
    const cellWidth = this.worldWidth / this.gridCols;
    const cellHeight = this.worldHeight / this.gridRows;

    const gridX = Math.floor(x / cellWidth);
    const gridY = Math.floor(y / cellHeight);

    // Clamp to valid grid range
    const clampedX = Math.max(0, Math.min(gridX, this.gridCols - 1));
    const clampedY = Math.max(0, Math.min(gridY, this.gridRows - 1));

    const zone = this.zones.find(
      (z) => z.gridX === clampedX && z.gridY === clampedY
    );
    return zone?.id ?? null;
  }

  /**
   * Get the current zone ID the player is in.
   */
  getCurrentZoneId(): string | null {
    return this.currentZoneId;
  }

  /**
   * Get the current zone object.
   */
  getCurrentZone(): ZoneGridEntry | null {
    if (!this.currentZoneId) return null;
    return this.zones.find((z) => z.id === this.currentZoneId) ?? null;
  }

  /**
   * Get all zones that are adjacent to the given zone (including diagonals).
   */
  getAdjacentZones(zoneId: string): ZoneGridEntry[] {
    const zone = this.zones.find((z) => z.id === zoneId);
    if (!zone) return [];

    return this.zones.filter((z) => {
      if (z.id === zoneId) return false;
      const dx = Math.abs(z.gridX - zone.gridX);
      const dy = Math.abs(z.gridY - zone.gridY);
      return dx <= 1 && dy <= 1;
    });
  }

  /**
   * Get the zones currently visible in the viewport (based on camera position and viewport size).
   * This is useful for determining what percentage of visible zones are placeholders.
   */
  getVisibleZones(
    cameraX: number,
    cameraY: number,
    viewportWidth: number,
    viewportHeight: number
  ): ZoneGridEntry[] {
    const cellWidth = this.worldWidth / this.gridCols;
    const cellHeight = this.worldHeight / this.gridRows;

    // Camera bounds (top-left to bottom-right)
    const left = cameraX - viewportWidth / 2;
    const right = cameraX + viewportWidth / 2;
    const top = cameraY - viewportHeight / 2;
    const bottom = cameraY + viewportHeight / 2;

    return this.zones.filter((z) => {
      const zoneLeft = z.gridX * cellWidth;
      const zoneRight = zoneLeft + cellWidth;
      const zoneTop = z.gridY * cellHeight;
      const zoneBottom = zoneTop + cellHeight;

      // Check AABB overlap
      return zoneLeft < right && zoneRight > left && zoneTop < bottom && zoneBottom > top;
    });
  }

  // ─── Placeholder Zone Management ───

  /**
   * Mark a zone as using placeholder visuals (asset load failed).
   */
  markAsPlaceholder(zoneId: string): void {
    this.placeholderZones.add(zoneId);
  }

  /**
   * Remove placeholder status (asset recovered/loaded successfully).
   */
  clearPlaceholder(zoneId: string): void {
    this.placeholderZones.delete(zoneId);
  }

  /**
   * Check if a specific zone is in placeholder state.
   */
  isPlaceholderZone(zoneId: string): boolean {
    return this.placeholderZones.has(zoneId);
  }

  /**
   * Calculate the fraction of currently visible zones that are in placeholder state.
   * Used by Soft_Guide to reduce guidance toward broken areas (Requirement 1.10).
   */
  getPlaceholderFraction(
    cameraX: number,
    cameraY: number,
    viewportWidth: number,
    viewportHeight: number
  ): number {
    const visible = this.getVisibleZones(cameraX, cameraY, viewportWidth, viewportHeight);
    if (visible.length === 0) return 0;

    const placeholderCount = visible.filter((z) => this.placeholderZones.has(z.id)).length;
    return placeholderCount / visible.length;
  }

  /**
   * Check if a target position is in a placeholder zone.
   * Used by guidance systems to avoid directing children toward broken areas.
   */
  isPositionInPlaceholderZone(x: number, y: number): boolean {
    const zoneId = this.getZoneAtPosition(x, y);
    return zoneId !== null && this.placeholderZones.has(zoneId);
  }

  /**
   * Get all zones that are NOT in placeholder state and are accessible from the player's current position.
   * Useful for guidance fallback.
   */
  getHealthyZones(): ZoneGridEntry[] {
    return this.zones.filter((z) => !this.placeholderZones.has(z.id));
  }
}
